/**
 * Ant Design Tour/Table (and similar) can trigger the browser's
 * "ResizeObserver loop completed with undelivered notifications" notice.
 * CRA + react-refresh show that as a fullscreen overlay even though it is benign.
 *
 * 1. Defer ResizeObserver callbacks by a frame so layout settles (prevents the notice).
 * 2. Swallow the error event if it still fires.
 * 3. Hide react-error-overlay iframe if it still appears with that message.
 */

const RESIZE_OBSERVER_NOISE =
  /ResizeObserver loop (completed with undelivered notifications|limit exceeded)/i;

const isResizeObserverNoise = (message: unknown): boolean =>
  typeof message === "string" && RESIZE_OBSERVER_NOISE.test(message);

const patchResizeObserver = (): void => {
  const OriginalResizeObserver = window.ResizeObserver;
  if (!OriginalResizeObserver) {
    return;
  }

  window.ResizeObserver = class PatchedResizeObserver extends (
    OriginalResizeObserver
  ) {
    constructor(callback: ResizeObserverCallback) {
      super((entries, observer) => {
        // Defer so we never mutate layout in the same frame as observation.
        window.requestAnimationFrame(() => {
          callback(entries, observer);
        });
      });
    }
  };
};

const hideReactErrorOverlayIfNoise = (): void => {
  // react-error-overlay mounts an iframe at document.body
  const iframes = document.querySelectorAll("iframe");
  iframes.forEach((iframe) => {
    try {
      const doc = iframe.contentDocument;
      const text = doc?.body?.textContent ?? "";
      if (isResizeObserverNoise(text)) {
        iframe.remove();
      }
    } catch {
      // cross-origin — ignore
    }
  });
};

/**
 * Call once at app bootstrap (before React render).
 */
export const suppressResizeObserverOverlayError = (): (() => void) => {
  patchResizeObserver();

  const onError = (event: ErrorEvent) => {
    if (
      isResizeObserverNoise(event.message) ||
      isResizeObserverNoise(event.error?.message)
    ) {
      event.stopImmediatePropagation();
      event.preventDefault();
      // Overlay may already be painting — clear on next tick
      window.setTimeout(hideReactErrorOverlayIfNoise, 0);
    }
  };

  window.addEventListener("error", onError, true);

  const mutationObserver = new MutationObserver(() => {
    hideReactErrorOverlayIfNoise();
  });
  mutationObserver.observe(document.documentElement, {
    childList: true,
    subtree: true,
  });

  return () => {
    window.removeEventListener("error", onError, true);
    mutationObserver.disconnect();
  };
};
